import { EnumButtonName } from '@calm/evexia-analytics';
import playerMessages from '@rhg/lang/messages/player';
import {
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    SvgIconProps,
} from '@rhg/libs/material';
import { Checkmark } from '@rhg/libs/material/icons';
import {
    formatPlaybackRate,
    NORMAL_PLAYBACK_RATE,
    PLAYBACK_RATES,
} from '@rhg/utils/media';
import { MouseEvent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { SpeedGlyph } from './speed-glyph';

export interface PlaybackSpeedButtonProps {
    playbackRate?: number;
    onChange: (rate: number) => void;
    disabled?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
}

export function PlaybackSpeedButton({
    playbackRate = NORMAL_PLAYBACK_RATE,
    onChange,
    disabled = false,
    onOpenChange,
}: PlaybackSpeedButtonProps) {
    const { formatMessage } = useIntl();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const isOpen = Boolean(anchorEl);
    const isActive = playbackRate !== NORMAL_PLAYBACK_RATE;

    // Notify the parent if this component unmounts while the menu is open,
    // preventing stuck auto-hide suppression.
    useEffect(() => () => onOpenChange?.(false), [onOpenChange]);

    const glyphColor: SvgIconProps['color'] = disabled
        ? 'disabled'
        : isActive
        ? 'info'
        : 'inherit';

    const handleOpen = (e: MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
        onOpenChange?.(true);
    };

    const handleClose = () => {
        setAnchorEl(null);
        onOpenChange?.(false);
    };

    const handleSelect = (rate: number) => {
        onChange(rate);
        handleClose();
    };

    const optionLabel = (rate: number) =>
        formatMessage(playerMessages['player.playback_speed.value'], {
            speed: formatPlaybackRate(rate),
        });

    return (
        <>
            <IconButton
                trackButton={EnumButtonName.PlaybackSettings}
                variant="contained"
                size="medium"
                disabled={disabled}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-label={formatMessage(
                    playerMessages['player.playback_speed.current'],
                    { speed: formatPlaybackRate(playbackRate) }
                )}
                onClick={handleOpen}
            >
                <SpeedGlyph rate={playbackRate} color={glyphColor} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                {PLAYBACK_RATES.map((rate) => {
                    const isSelected = rate === playbackRate;
                    return (
                        <MenuItem
                            key={rate}
                            trackButton={EnumButtonName.PlaybackSpeed}
                            trackProps={{
                                PlaybackSpeed: `${rate}x`,
                                PreviousPlaybackSpeed: `${playbackRate}x`,
                            }}
                            role="menuitemradio"
                            aria-checked={isSelected}
                            selected={isSelected}
                            aria-label={formatMessage(
                                playerMessages['player.playback_speed.option'],
                                { speed: formatPlaybackRate(rate) }
                            )}
                            onClick={() => handleSelect(rate)}
                        >
                            <ListItemIcon>
                                {isSelected && <Checkmark size="small" />}
                            </ListItemIcon>
                            {optionLabel(rate)}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
}
